import { useState, useEffect, useCallback, RefObject } from 'react';
import { loadGoogleMapsPlaces } from './loadGoogleMapsPlaces';

type Autocomplete = google.maps.places.Autocomplete;
type PlaceResult = google.maps.places.PlaceResult;
type ComponentRestrictions = google.maps.places.ComponentRestrictions;

export const useAddressAutocomplete = (
  inputRef: RefObject<HTMLInputElement | null>,
  countryCode?: string | undefined
) => {
  const [autocomplete, setAutocomplete] = useState<Autocomplete | undefined>(undefined);
  const [place, setPlace] = useState<PlaceResult | undefined>(undefined);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    const componentRestrictions: ComponentRestrictions | undefined = countryCode
      ? {
          country: countryCode
        }
      : undefined;

    if (componentRestrictions) {
      autocomplete?.setComponentRestrictions(componentRestrictions);
    }
  }, [autocomplete, countryCode]);

  const createAutocomplete = useCallback(() => {
    if (inputRef.current)
      setAutocomplete(
        new window.google.maps.places.Autocomplete(inputRef.current, {
          fields: ['address_components']
        })
      );
  }, [inputRef]);

  useEffect(() => {
    if (!autocomplete) {
      loadGoogleMapsPlaces(createAutocomplete);
    }
  }, [autocomplete, createAutocomplete]);

  const handlePlaceChange = useCallback(() => {
    const place = autocomplete?.getPlace();

    if (place) {
      setPlace(place);
    }
  }, [autocomplete]);

  useEffect(() => {
    const listener = autocomplete?.addListener('place_changed', handlePlaceChange);
    return () => listener?.remove();
  }, [autocomplete, handlePlaceChange]);

  const handleFieldFocusChange = useCallback(
    (focused: boolean) => isFocused !== focused && setIsFocused(focused),
    [isFocused]
  );

  useEffect(() => {
    inputRef?.current?.addEventListener('focus', () => handleFieldFocusChange(true));
    inputRef?.current?.addEventListener('blur', () => handleFieldFocusChange(false));
  }, [handleFieldFocusChange, inputRef]);

  return { place, isFocused };
};
